// src/components/Patient/displayPrintView.component.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useGlobalContext } from "../../../Context/global.context";
import { getUserFromToken } from '../../../Context/functions';
import { useLocation, useNavigate } from 'react-router-dom';

// ---------- helpers ----------
const norm = (s) => (s ?? "").toString().trim();

// Legacy parser (kept as fallback only)
const parseLegacyMeds = (str) => {
  if (!str) return [];
  return String(str)
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => {
      const m = t.match(/^\s*([^[\]{},]+?)\s*[\[\{]\s*([^\]\}]*)\s*[\]\}]\s*$/);
      if (m) return { name: m[1].trim(), category: "No Category", dose: String(m[2] ?? "").trim() };
      const parts = t.split(":").map((x) => x.trim());
      if (parts.length >= 3) return { name: parts[0], category: parts[1] || "No Category", dose: parts[2] || "" };
      return { name: t, category: "No Category", dose: "" };
    });
};

const parseMedicationObjects = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const extractDinLikeValue = (...values) => {
  for (const value of values) {
    const raw = String(value ?? "").trim();
    if (!raw) continue;
    const digits = raw.replace(/\D+/g, "").slice(0, 8);
    if (digits) return digits;
  }
  return "";
};

const cleanDoctorName = (value) =>
  String(value ?? "")
    .replace(/\.\.\.\s*$/, "")
    .trim() || "NA";

// ID-based helpers (mirrors PatientMeds)
const readMasterId = (m) => String(m?.ID ?? m?.id ?? "");
const readMasterDIN = (m) => String(m?.DIN ?? m?.din ?? m?.medication_din ?? "").replace(/\D+/g, "").slice(0, 8);
const readMasterName = (m) => String(m?.medication_name ?? m?.medication ?? m?.name ?? "");
const readMasterDose = (m) => String(m?.medication_dose ?? m?.defaultDose ?? m?.dose ?? "");
const readMasterCat = (m) => String(m?.medication_cat ?? m?.category ?? "");
const parseIdCSV = (str) => (str || "").split(",").map((t) => t.trim()).filter(Boolean);

// Parse condition codes CSV -> array of UPPER codes
const parseConditionCodes = (csv) =>
  (csv || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => s.toUpperCase());

// Build code -> label map from context.conditionData
const buildConditionMap = (conditionData) => {
  const list = Array.isArray(conditionData)
    ? conditionData
    : conditionData && typeof conditionData === "object"
      ? Object.values(conditionData)
      : [];
  const map = new Map();
  for (const c of list) {
    const label = c?.conditionName ?? c?.name ?? String(c ?? "");
    const code = (c?.conditionCode ?? c?.code ?? "").toUpperCase();
    if (code) map.set(code, label);
  }
  return map;
};

// Labs to display (keys correspond to fields on patient_history rows)
const LAB_FIELDS = [
  ["cholesterol", "Cholesterol"],
  ["triglyceride", "Triglyceride"],
  ["hdl", "HDL"],
  ["ldl", "LDL"],
  ["nonHdl", "Non-HDL"],
  ["cholesterolHdlRatio", "Chol/HDL Ratio"],
  ["creatineKinase", "Creatine Kinase"],
  ["alanineAminotransferase", "ALT"],
  ["lipoproteinA", "Lipoprotein(a)"],
  ["apolipoproteinB", "ApoB"],
  ["natriureticPeptideB", "BNP"],
  ["urea", "Urea"],
  ["creatinine", "Creatinine"],
  ["gfr", "GFR"],
  ["albumin", "Albumin"],
  ["sodium", "Sodium"],
  ["potassium", "Potassium"],
  ["vitaminB12", "Vitamin B12"],
  ["ferritin", "Ferritin"],
  ["hemoglobinA1C", "Hemoglobin A1C"],
  ["urineAlbumin", "Urine Albumin"],
  ["albuminCreatinineRatio", "Alb/Cr Ratio"],
];

// Show "-" for truly blank values but keep zeroes like 0 or "0.00"
const printVal = (v) => (v === null || v === undefined || String(v).trim() === "" ? "-" : String(v));

// Mask for demos: "Patient ####"
const demoPatientLabel = (healthNumber) => {
  const digits = String(healthNumber || "").replace(/\D/g, "");
  const first4 = digits.slice(0, 4) || "XXXX";
  return `Patient ${first4}`;
};

// NEW: mask 3 middle digits of HCN when printing privately -> 123 XXX 7890
const maskHealthNumber3 = (hcn) => {
  const digits = String(hcn || "").replace(/\D/g, "");
  if (!digits) return hcn || "—";
  const first3 = digits.slice(0, 3);
  const last4 = digits.slice(-4);
  return `${first3} XXX ${last4}`;
};

const getTodayDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getPrintedTimestamp = () => {
  const now = new Date();
  const date = now.toLocaleDateString("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const time = now.toLocaleTimeString("en-CA", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${date} ${time}`;
};

const parseMedsCheckJson = (value) => {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith("{")) return null;

  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === "object" && parsed.runtime_metadata ? parsed : null;
  } catch {
    return null;
  }
};

const listOrEmpty = (items) => (Array.isArray(items) ? items.filter(Boolean) : []);

const RenderBullets = ({ items, empty = "None documented." }) => {
  const list = listOrEmpty(items);
  if (!list.length) return <div className="text-muted"><em>{empty}</em></div>;

  return (
    <ul className="mb-0 ps-3">
      {list.map((item, idx) => (
        <li key={`${String(item).slice(0, 24)}_${idx}`}>{String(item)}</li>
      ))}
    </ul>
  );
};

const SignatureBlock = () => (
  <div className="mt-4 pt-2">
    <div className="fw-bold border-bottom mb-2">Signature Block</div>
    <div className="mb-3"><strong>Reviewed by:</strong> Mark Brady RPh, OCP License 218305</div>
    <div className="mb-3"><strong>Pharmacist Signature:</strong> ________________________________</div>
    <div className="mb-2">
      <strong>Patient Signature:</strong> ________________________________
      <span className="ms-4"><strong>Date:</strong> __________________</span>
    </div>
  </div>
);

const PatientCopy = ({ recommendations = [], recommendationText = "", printedTimestamp }) => (
  <div style={{ breakBefore: "page", pageBreakBefore: "always", fontSize: "1rem", lineHeight: 1.45 }}>
    <div className="mb-2 h4" style={{ borderBottom: "2px solid" }}>Patient Copy</div>
    <div className="mb-2 fw-bold">Recommendations</div>
    {recommendations.length ? (
      recommendations.map((rec, idx) => (
        <div key={rec.recommendation_id || idx} className="mb-2 pb-1 border-bottom">
          <div><strong>{idx + 1}. {rec.recommendation_text}</strong></div>
          {rec.monitoring_required?.length ? (
            <div><strong>Monitoring:</strong> {rec.monitoring_required.join("; ")}</div>
          ) : null}
        </div>
      ))
    ) : recommendationText ? (
      <div style={{ whiteSpace: "pre-wrap" }}>{recommendationText}</div>
    ) : (
      <div className="text-muted"><em>No recommendations documented.</em></div>
    )}
    <div className="mt-4 pt-2 border-top">
      <div><strong>Pharmacist:</strong> Mark Brady RPh</div>
      <div><strong>OCP License:</strong> 218305</div>
      <div className="mt-2 small"><strong>Printed:</strong> {printedTimestamp}</div>
    </div>
  </div>
);

const extractRecommendationsText = (text = "") => {
  const raw = String(text || "");
  const match = raw.match(/(?:^|\n)Recommendations\n([\s\S]*?)(?:\n\nMonitoring Plan|\n\nFollow-Up Plan|\n\nPatient Education|\n\nPharmacist Review Flags|$)/i);
  return match ? match[1].trim() : "";
};

const MedsCheckJsonDocument = ({ aiOutput, patientName, printedTimestamp, fallbackMedications = [] }) => {
  const metadata = aiOutput?.runtime_metadata || {};
  const patientContext = aiOutput?.patient_context || {};
  const assessment = aiOutput?.clinical_assessment || {};
  const recommendations = listOrEmpty(aiOutput?.recommendations);
  const monitoring = aiOutput?.monitoring_plan || {};
  const followup = aiOutput?.followup_plan || {};
  const validationFlags = listOrEmpty(aiOutput?.validation_flags);
  const meds = listOrEmpty(patientContext?.medications);
  const renderedMeds = meds.length
    ? meds.map((m) => ({
        medication: [m.generic_name, m.strength].filter(Boolean).join(" "),
        detail: [m.frequency, m.indication].filter(Boolean).join(" | "),
      }))
    : fallbackMedications.map((m) => ({
        medication: m.medication || m.name || "NA",
        detail: [m.firstFill && `First ${m.firstFill}`, m.lastFill && `Last ${m.lastFill}`].filter(Boolean).join(" | "),
      }));

  return (
    <div style={{ fontSize: "0.94rem", lineHeight: 1.35 }}>
      <div className="mb-2 h4" style={{ borderBottom: "2px solid" }}>MedsCheck Annual</div>

      <div className="row g-2 mb-2">
        <div className="col-24"><strong>Patient:</strong> {patientName || "—"}</div>
        <div className="col-12"><strong>Age:</strong> {patientContext.patient_age || "—"}</div>
        <div className="col-12"><strong>Sex:</strong> {patientContext.patient_sex || "—"}</div>
        <div className="col-24"><strong>Review Date:</strong> {metadata.execution_timestamp ? metadata.execution_timestamp.slice(0, 10) : "—"}</div>
        <div className="col-24"><strong>Pharmacist:</strong> Mark Brady RPh, OCP 218305</div>
      </div>

      <div className="mb-2">
        <div className="fw-bold border-bottom mb-1">MedsCheck Eligibility Confirmation</div>
        <div className="row g-1">
          <div className="col-24">MedsCheck Annual confirmed</div>
          <div className="col-24">Consent and OHIP eligibility confirmed</div>
          <div className="col-24">Minimum medication criteria met</div>
          <div className="col-24">Encounter requirements met</div>
        </div>
      </div>

      <div className="mb-2">
        <div className="fw-bold border-bottom mb-1">Clinical Overview</div>
        <div className="row g-2">
          <div className="col-24">
            <strong>Conditions</strong>
            <RenderBullets items={patientContext.conditions} empty="No conditions documented." />
          </div>
          <div className="col-24">
            <strong>Risk Domains</strong>
            <RenderBullets items={assessment.primary_risk_domains} />
          </div>
        </div>
      </div>

      <div className="mb-2">
        <div className="fw-bold border-bottom mb-1">Medication Review</div>
        {renderedMeds.length ? (
          <div>
            {renderedMeds.map((m, idx) => (
              <div key={`${m.medication}_${idx}`} className="border-bottom py-1">
                <strong>{m.medication || "NA"}</strong>
                {m.detail ? <span className="ms-2">{m.detail}</span> : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-muted"><em>No medications documented.</em></div>
        )}
      </div>

      <div className="mb-2">
        <div className="fw-bold border-bottom mb-1">Drug Therapy Problems / Gaps</div>
        <RenderBullets items={[...listOrEmpty(assessment.identified_therapy_gaps), ...listOrEmpty(assessment.safety_flags)]} />
      </div>

      <div className="mb-2">
        <div className="fw-bold border-bottom mb-1">Recommendations</div>
        {recommendations.length ? (
          recommendations.map((rec, idx) => (
            <div key={rec.recommendation_id || idx} className="mb-2 pb-1 border-bottom">
              <div><strong>{idx + 1}. {rec.recommendation_text}</strong></div>
              {rec.clinical_rationale ? <div><strong>Rationale:</strong> {rec.clinical_rationale}</div> : null}
              {rec.implementation_rationale ? <div><strong>Implementation:</strong> {rec.implementation_rationale}</div> : null}
              <div><strong>Monitoring:</strong> {listOrEmpty(rec.monitoring_required).join("; ") || "None specified."}</div>
              <div><strong>Priority/Confidence:</strong> {rec.priority_level || "—"} / {rec.confidence_level || "—"}</div>
            </div>
          ))
        ) : (
          <div className="text-muted"><em>No recommendations generated.</em></div>
        )}
      </div>

      <div className="mb-2">
        <div className="fw-bold border-bottom mb-1">Monitoring Plan</div>
        <RenderBullets items={[...listOrEmpty(monitoring.required_monitoring), ...listOrEmpty(monitoring.recommended_lab_intervals)]} />
      </div>

      <div className="mb-2">
        <div className="fw-bold border-bottom mb-1">Patient Education</div>
        <RenderBullets
          items={[
            "Reviewed medication adherence and practical medication use.",
            "Patient encouraged to contact the pharmacy with medication questions or concerns.",
          ]}
        />
      </div>

      <div className="mb-2">
        <div className="fw-bold border-bottom mb-1">Follow-Up Plan</div>
        <div><strong>Timeline:</strong> {followup.recommended_followup_interval || monitoring.followup_timeline || "As clinically appropriate."}</div>
        {followup.recommended_followup_reason ? <div><strong>Reason:</strong> {followup.recommended_followup_reason}</div> : null}
        <RenderBullets items={[...listOrEmpty(followup.pending_actions), ...listOrEmpty(followup.reassessment_triggers)]} empty="No pending actions documented." />
      </div>

      {validationFlags.length ? (
        <div className="mb-2">
          <div className="fw-bold border-bottom mb-1">Pharmacist Review Flags</div>
          <RenderBullets items={validationFlags.map((flag) => `${flag.severity}: ${flag.description}`)} />
        </div>
      ) : null}

      <SignatureBlock />

      <div className="mt-3 pt-2 border-top" style={{ fontSize: "0.9rem" }}>
        <strong>MedsCheck Printed:</strong> {printedTimestamp}
      </div>

      <PatientCopy recommendations={recommendations} printedTimestamp={printedTimestamp} />
    </div>
  );
};

const PrintLabView = () => {

  const [user, setUser] = React.useState(null);
  const [patientDB, setPatientDB] = useState(null);
  const [historyDB, setHistoryDB] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const printMode = location?.state?.printMode === "medcheck" ? "medcheck" : "doctor";

  useEffect(() => {
    console.log(user);
  }, [user]);

  useEffect(() => {
    const fetchUser = async () => {
      const userData = await getUserFromToken();
      return userData;
    };
    fetchUser().then((userT) => {
      if (userT) {
        console.log('User data:', userT);
        setUser(userT);
        setPatientDB(userT.patientTable);
        setHistoryDB(userT.historyTable);
      }
      if (!userT) {
        // If no user is found, redirect to sign-in page
        navigate('/signin');
        return;
      }
    });
  }, []);

  const { activePatient, setActivePatient, setVisibleBox, conditionData, privateMode, medsArray, updateMedsArray } = useGlobalContext();
  const [fresh, setFresh] = useState(activePatient || {});
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [medCheckComplete, setMedCheckComplete] = useState(false);
  const [markingMedCheckComplete, setMarkingMedCheckComplete] = useState(false);
  const savedMedCheckDate = String(
    activePatient?.medCheckDate ?? ""
  ).trim();
  const [printedTimestamp] = useState(() => getPrintedTimestamp());

  // Refresh latest patient on mount / when ID changes; also set a short history snapshot
  useEffect(() => {
    // 🚫 Don’t run until we know who the user is (so tables are correct)
    if (!user) {
      setFresh(activePatient || {});
      setHistory([]);
      return;
    }

    const id = activePatient?.id ? Number(activePatient.id) : null;
    const hcn = activePatient?.healthNumber
      ? String(activePatient.healthNumber).replace(/\s+/g, " ").trim()
      : null;

    if (!id) {
      setFresh(activePatient || {});
      setHistory([]);
      return;
    }

    // Pull meds master list (only if empty) with the CORRECT tables
    if (Array.isArray(medsArray) && medsArray.length === 0) {
      fetch("https://gdmt.ca/PHP/noDB.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        cache: "no-store",
        body: JSON.stringify({ script: "getMeds" }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (Array.isArray(data?.meds)) {
            updateMedsArray?.(data.meds);
          }
        })
        .catch(() => { });
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const res = await fetch("https://gdmt.ca/PHP/special.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          keepalive: true,
          cache: "no-store",
          body: JSON.stringify({
            script: "getPatientById",
            patientID: id,
            healthNumber: hcn,
            patientDB: patientDB || 'Patient',
            historyDB: historyDB || 'Patient_History'
          }),
        });

        const text = await res.text();
        let data = null;
        try { data = JSON.parse(text); } catch { }
        console.log('Fetched patient data:', data);
        const latest = res.ok && data && data.patient ? data.patient : activePatient;
        const last3 = Array.isArray(data?.history) ? data.history.slice(0, 3) : [];

        if (!cancelled) {
          setFresh(latest || {});
          setActivePatient?.(latest || {});
          setHistory(last3);
        }
      } catch {
        if (!cancelled) {
          setFresh(activePatient || {});
          setHistory([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // 👇 Re-run when patient changes OR when the user’s table names change
  }, [activePatient?.id, patientDB, historyDB, user]);

  useEffect(() => {
    if (printMode !== "medcheck") {
      setMedCheckComplete(false);
      return;
    }

    const isComplete =
      savedMedCheckDate !== "" ||
      String(fresh?.medCheckSearch ?? activePatient?.medCheckSearch ?? "")
        .trim()
        .toLowerCase() === "yes";

    setMedCheckComplete(isComplete);
  }, [activePatient?.medCheckSearch, fresh?.medCheckSearch, printMode, savedMedCheckDate]);


  // Derived display data
  const patientName =
    fresh?.clientName ||
    fresh?.name ||
    (fresh?.firstName && fresh?.lastName ? `${fresh.firstName} ${fresh.lastName}` : "") ||
    fresh?.lastFirstName ||
    "—";

  const isPrivate = Boolean(privateMode);
  const headerName = isPrivate ? demoPatientLabel(fresh?.healthNumber) : (patientName || "—");
  const displayHCN = isPrivate ? maskHealthNumber3(fresh?.healthNumber) : (fresh?.healthNumber || "—");

  const paymentMethod = String(
    fresh?.paymentMethod ?? fresh?.paymentMethof ?? "CASH"
  );

  const doctorNote = fresh?.patientNote ?? "";
  const medCheckNote = fresh?.medCheckData ?? "";
  const medCheckJson = useMemo(() => parseMedsCheckJson(medCheckNote), [medCheckNote]);
  const medCheckRecommendationText = useMemo(() => extractRecommendationsText(medCheckNote), [medCheckNote]);

  // Conditions list
  const codeToLabel = useMemo(() => buildConditionMap(conditionData), [conditionData]);
  const patientConditionCodes = useMemo(
    () => parseConditionCodes(fresh?.conditionData || fresh?.conditions || ""),
    [fresh?.conditionData, fresh?.conditions]
  );
  const patientConditions = useMemo(
    () => patientConditionCodes.map((code) => ({ code, label: codeToLabel.get(code) || code })),
    [patientConditionCodes, codeToLabel]
  );

  // ---------- Medications (ID CSV → resolve via medsArray) ----------
  const master = Array.isArray(medsArray) ? medsArray : [];
  const masterById = useMemo(() => {
    const map = new Map();
    for (const m of master) map.set(readMasterId(m), m);
    return map;
  }, [master]);
  const masterByDin = useMemo(() => {
    const map = new Map();
    for (const m of master) {
      const din = readMasterDIN(m);
      if (din) map.set(din, m);
    }
    return map;
  }, [master]);

  const patientMeds = useMemo(() => {
    const tokens = parseIdCSV(fresh?.medsData);
    if (tokens.length > 0) {
      return tokens.map((token) => {
        const din = String(token).replace(/\D+/g, "").slice(0, 8);
        const m = masterByDin.get(din) || masterById.get(String(token));
        return {
          name: readMasterName(m) || `DIN ${token}`,
          category: readMasterCat(m) || "No Category",
          dose: readMasterDose(m) || "",
          din: readMasterDIN(m) || din,
        };
      });
    }
    // Fallback for legacy data
    return parseLegacyMeds(fresh?.medsData);
  }, [fresh?.medsData, masterByDin, masterById]);

  const medCheckMeds = useMemo(() => {
    const medicationObjects = parseMedicationObjects(fresh?.medications);
    if (medicationObjects.length > 0) {
      return medicationObjects.map((m, idx) => {
        const medicationText = String(m?.medication || m?.name || m?.drugName || "").trim();
        const inlineDin = extractDinLikeValue(medicationText);
        const explicitDin = extractDinLikeValue(m?.din, m?.DIN, m?.medication_din);
        const matchedMaster = masterByDin.get(explicitDin || inlineDin);
        const resolvedMedication =
          matchedMaster && (!medicationText || /^din\b/i.test(medicationText))
            ? readMasterName(matchedMaster)
            : medicationText;

        return {
          key: `medcheck_json_${idx}`,
          medication: String(resolvedMedication || medicationText || "NA").trim() || "NA",
          doctorName: cleanDoctorName(m?.doctorName || m?.doctor || m?.prescriber || "NA"),
          firstFill: String(m?.firstFill || "NA").trim() || "NA",
          lastFill: String(m?.lastFill || "NA").trim() || "NA",
        };
      });
    }

    const legacyList = parseLegacyMeds(fresh?.medications);
    if (legacyList.length > 0) {
      return legacyList.map((m, idx) => ({
        key: `medcheck_legacy_medications_${idx}`,
        medication: String(m?.name || "NA").trim() || "NA",
        doctorName: "NA",
        firstFill: "NA",
        lastFill: "NA",
      }));
    }

    return patientMeds.map((m, idx) => ({
      key: `medcheck_fallback_${idx}`,
      medication: String(m?.name || "NA").trim() || "NA",
      doctorName: "NA",
      firstFill: "NA",
      lastFill: "NA",
    }));
  }, [fresh?.medications, patientMeds, masterByDin]);

  const medCheckBodyStyle = { fontSize: "0.9rem", lineHeight: 1.35 };
  const labResultsBodyStyle = { fontSize: "0.82rem", lineHeight: 1.2 };

  const handleMarkMedCheckComplete = async () => {
    if (!activePatient?.id || markingMedCheckComplete || medCheckComplete) return;

    setMarkingMedCheckComplete(true);

    try {
      const userData = user || (await getUserFromToken());
      const today = getTodayDateString();
      const updateRequests = [
        fetch("https://gdmt.ca/PHP/special.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          keepalive: true,
          body: JSON.stringify({
            script: "updatePatientAiField",
            patientID: activePatient.id,
            fieldName: "medCheckDate",
            value: today,
            patientDB: userData?.patientTable || "Patient",
            historyDB: userData?.historyTable || "Patient_History",
          }),
        }),
        fetch("https://gdmt.ca/PHP/special.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          keepalive: true,
          body: JSON.stringify({
            script: "updatePatientAiField",
            patientID: activePatient.id,
            fieldName: "medCheckSearch",
            value: "Yes",
            patientDB: userData?.patientTable || "Patient",
            historyDB: userData?.historyTable || "Patient_History",
          }),
        }),
      ];

      const responses = await Promise.all(updateRequests);
      const payloads = await Promise.all(
        responses.map(async (res) => {
          try {
            return await res.json();
          } catch {
            return null;
          }
        })
      );

      const ok = responses.every((res, idx) => res.ok && payloads[idx]?.success === true);
      if (!ok) return;

      setMedCheckComplete(true);
      setActivePatient?.((prev) =>
        prev
          ? { ...prev, medCheckDate: today, medCheckSearch: "Yes", medCheckComplete: true }
          : prev
      );
      setFresh((prev) =>
        prev
          ? { ...prev, medCheckDate: today, medCheckSearch: "Yes", medCheckComplete: true }
          : prev
      );
    } finally {
      setMarkingMedCheckComplete(false);
    }
  };

  // Build 3 “date” columns from history (headers only; no dates in cells)
  const { headings, cols } = useMemo(() => {
    const sorted = Array.isArray(history)
      ? [...history].sort((a, b) => new Date(b?.orderDate || 0) - new Date(a?.orderDate || 0))
      : [];
    const c0 = sorted[0] || {};
    const c1 = sorted[1] || {};
    const c2 = sorted[2] || {};
    return {
      headings: [
        c0?.orderDate ? c0.orderDate : "Not Entered",
        c1?.orderDate ? c1.orderDate : "Not Entered",
        c2?.orderDate ? c2.orderDate : "Not Entered",
      ],
      cols: [c0, c1, c2],
    };
  }, [history]);

  return (
    <div className="mx-auto" style={{ width: "210mm", minHeight: "297mm", maxWidth: "100%" }}>
      <div className="row mt-3 mb-3 d-print-none">
        <div className="col-48 d-flex gap-2">
          <button
            className="btn btn-outline-secondary"
            style={{ flex: 1 }}
            onClick={() => navigate("/dashboard")}
          >
            Back
          </button>
          {printMode === "medcheck" && (
            <button
              className={`btn ${medCheckComplete ? "btn-success" : "btn-primary"}`}
              style={{ flex: 1.35 }}
              onClick={handleMarkMedCheckComplete}
              disabled={medCheckComplete || markingMedCheckComplete}
            >
              {medCheckComplete
                ? (savedMedCheckDate ? `MedsCheck ${savedMedCheckDate}` : "MedsCheck Complete")
                : markingMedCheckComplete
                  ? "Marking MedsCheck complete..."
                  : "Mark MedsCheck as complete"}
            </button>
          )}
          <button
            className="btn btn-outline-primary"
            style={{ flex: 1 }}
            onClick={() => window.print()}
          >
            Print
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="row align-items-start g-2 mb-2">
        <div className="col-36">
          <h1 className="h3 mb-1">{headerName}</h1>
          <div className="small">
            <strong>Health Number:</strong> {displayHCN}
          </div>
          {/* Payment Method */}
          <div className="small mt-1">
            <strong>Medication Coverage:</strong>{" "}
            <span className="fw-semibold" style={printMode === "medcheck" ? medCheckBodyStyle : undefined}>{paymentMethod}</span>
          </div>
        </div>
        <div className="col-12 d-print-none"></div>
      </div>

      {/* Doctor’s Note */}
      {printMode !== "medcheck" && (
        <div className="py-2 mb-3">
          <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="mb-2 h4" style={{ borderBottom: "2px solid" }}>
            Doctor’s Note
          </div>
          {loading && <span className="badge bg-info text-dark">Refreshing…</span>}
        </div>
          <div className="fs-7">
            {doctorNote ? doctorNote : <em>No note on file.</em>}
          </div>
        </div>
      )}

      <div className="mb-2 mt-2">
        <div className="mb-2 h4" style={{ borderBottom: "2px solid" }}>Lab Results</div>
        {(!history || history.length === 0) ? (
          <div className="border-bottom p-2" style={labResultsBodyStyle}><em>No labs on file.</em></div>
        ) : (
          <div className="row g-1" style={labResultsBodyStyle}>
            <div className="col-48">
              <div className="row g-1 fw-bold">
                <div className="col-12"></div>
                <div className="col-12 text-center">{headings[0]}</div>
                <div className="col-12 text-center">{headings[1]}</div>
                <div className="col-12 text-center">{headings[2]}</div>
              </div>
            </div>
            {LAB_FIELDS.map(([key, label]) => (
              <div key={key} className="col-48 border-bottom py-1">
                <div className="row g-1 align-items-baseline">
                  <div className="col-12 fw-bold text-truncate" title={label}>{label}</div>
                  <div className="col-12 text-center fw-semibold">
                    {cols[0][key] === 0.00 || cols[0][key] === "0.00" ? "-" : printVal(cols[0][key])}
                  </div>
                  <div className="col-12 text-center fw-semibold">
                    {cols[1][key] === 0.00 || cols[1][key] === "0.00" ? "-" : printVal(cols[1][key])}
                  </div>
                  <div className="col-12 text-center fw-semibold">
                    {cols[2][key] === 0.00 || cols[2][key] === "0.00" ? "-" : printVal(cols[2][key])}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Conditions */}
      <div className="mb-2">
        <div className="mb-2 h4" style={{ borderBottom: "2px solid" }}>Conditions</div>
        {patientConditions.length === 0 ? (
          <div className="border rounded p-2" style={printMode === "medcheck" ? medCheckBodyStyle : undefined}><em>No conditions on file.</em></div>
        ) : (
          <div className="row g-1" style={printMode === "medcheck" ? medCheckBodyStyle : undefined}>
            {patientConditions.map((c) => (
              <div key={c.code} className="col-16">
                <div className="p-1">
                  <div className="row g-1 align-items-baseline">
                    <div className="col-48 fw-semibold text-truncate" title={c.label}>
                      {c.label}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Medications */}
      <div className="mb-2 mt-2">
        <div className="mb-2 h4" style={{ borderBottom: "2px solid" }}>Medications</div>
        {printMode === "medcheck" ? (
          medCheckMeds.length === 0 ? (
            <div className="border rounded p-2" style={medCheckBodyStyle}><em>No medications on file.</em></div>
          ) : (
            <div className="row g-1" style={medCheckBodyStyle}>
              <div className="col-48 border-bottom fw-bold py-1">
                <div className="row g-1">
                  <div className="col-22">Medication</div>
                  <div className="col-6">First Fill</div>
                  <div className="col-6">Last Fill</div>
                  <div className="col-14">Doctor</div>
                </div>
              </div>
              {medCheckMeds.map((m) => (
                <div key={m.key} className="col-48 border-bottom py-1">
                  <div className="row g-1 align-items-baseline">
                    <div className="col-22" style={{ wordBreak: "break-word" }}>{m.medication || "NA"}</div>
                    <div className="col-6">{m.firstFill || "NA"}</div>
                    <div className="col-6">{m.lastFill || "NA"}</div>
                    <div className="col-14" style={{ wordBreak: "break-word" }}>{m.doctorName || "NA"}</div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : patientMeds.length === 0 ? (
          <div className="border rounded p-2"><em>No medications on file.</em></div>
        ) : (
          <div className="row g-1">
            {patientMeds.map((m, idx) => (
              <div key={`med_${idx}`} className="col-48">
                <div className="border-bottom p-1">
                  <div className="row g-0 align-items-baseline">
                    <div className="col-36 d-flex flex-nowrap align-items-baseline" style={{ minWidth: 0 }}>
                      <strong className="text-truncate" title={m.name} style={{ minWidth: 0 }}>
                        {m.name}
                      </strong>
                      {m.dose ? <span style={{ marginLeft: 5 }}>[{m.dose}]</span> : null}
                    </div>
                    <div className="col-12 text-end">
                      {m.category === "No Category" ? "" : m.category}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {printMode === "medcheck" ? (
        <>
          {medCheckJson ? (
            <MedsCheckJsonDocument
              aiOutput={medCheckJson}
              patientName={headerName}
              printedTimestamp={printedTimestamp}
              fallbackMedications={medCheckMeds}
            />
          ) : (
            <>
              <div className="mb-2">
                <div className="mb-2 h4" style={{ borderBottom: "2px solid" }}>MedsCheck</div>
                <div className="py-2 fs-7" style={{ fontSize: "1.08rem", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                  {medCheckNote ? medCheckNote : <em>No MedsCheck on file.</em>}
                </div>
              </div>
              <SignatureBlock />
              <div className="mt-3 pt-2 border-top" style={{ fontSize: "0.9rem" }}>
                <strong>MedsCheck Printed:</strong> {printedTimestamp}
              </div>
              <PatientCopy recommendationText={medCheckRecommendationText} printedTimestamp={printedTimestamp} />
            </>
          )}
        </>
      ) : (
        <div className="mb-2">
          <div className="mb-2 h4" style={{ borderBottom: "2px solid" }}>Recommendations</div>
          <div className="py-2" style={{ whiteSpace: "pre-wrap" }}>
            {fresh?.recommendations && String(fresh.recommendations).trim()
              ? fresh.recommendations
              : <em>No recommendations yet.</em>}
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintLabView;
